import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import {
	clearEuDdpRequestShipmentData,
	confirmEuDdpShipmentRequest,
	getEuDdpOrderRequest,
	requestEuDdpS10Request,
	setEuDdpConfirmShipmentError,
	setEuDdpS10Error,
} from 'redux/reducers/euDdp/reducer';
import {
	getEuDdpConfirmShipmentError,
	getEuDdpConfirmShipmentIsLoad,
	getEuDdpOrder,
	getEuDdpRequestShipmentData,
	getEuDdpRequestShipmentError,
	getEuDdpRequestShipmentIsLoad,
	getEuDdpS10Error,
	getEuDdpS10IsLoad,
} from 'redux/reducers/euDdp/selectors';
import { getEmAssignmentRequest } from 'redux/reducers/settings/reducer';
import { StepInitial } from './StepInitial';
import { StepPreview } from './StepPreview';
import { StepS10Expected } from './StepS10Expected';
import { EShipmentDialogStep } from './constants';
import { TRequestEuDdpShipmentDialogProps } from './types';

export const RequestEuDdpShipmentDialog: FC<TRequestEuDdpShipmentDialogProps> = ({
	open,
	setClose,
}) => {
	const dispatch = useDispatch();
	const params = useParams<{ orderId?: string }>();
	const { orderId } = params;
	const order = useSelector(getEuDdpOrder);
	const requestEuDdpShipmentLoader = useSelector(getEuDdpRequestShipmentIsLoad);
	const requestShipmentData = useSelector(getEuDdpRequestShipmentData);
	const requestShipmentError = useSelector(getEuDdpRequestShipmentError);
	const confirmEuDdpShipmentLoader = useSelector(getEuDdpConfirmShipmentIsLoad);
	const confirmShipmentError = useSelector(getEuDdpConfirmShipmentError);
	const s10Loading = useSelector(getEuDdpS10IsLoad);
	const s10Error = useSelector(getEuDdpS10Error);

	const [shipmentDialogStep, setShipmentDialogStep] = useState<EShipmentDialogStep>(
		EShipmentDialogStep.INITIAL,
	);
	const [s10Number, setS10Number] = useState('');
	// Total and bank fee shown on the preview: sent with confirm_shipment / s10, the payment gateway charges
	// exactly the shown amount (a changed amount is refused instead of charged).
	const [shownAmounts, setShownAmounts] = useState<{ totalAmount: number; bankFee: number } | null>(
		null,
	);
	const prevConfirmShipmentLoading = useRef(false);
	const prevRequestShipmentLoading = useRef(false);
	const prevS10Loading = useRef(false);
	const actionLoading = requestEuDdpShipmentLoader || confirmEuDdpShipmentLoader || s10Loading;

	useEffect(() => {
		if (!open) {
			setShipmentDialogStep(EShipmentDialogStep.INITIAL);
			setS10Number('');
			setShownAmounts(null);
			return;
		}
		dispatch(getEmAssignmentRequest());
	}, [open, dispatch]);

	useEffect(() => {
		if (
			requestShipmentData &&
			open &&
			!requestEuDdpShipmentLoader &&
			shipmentDialogStep === EShipmentDialogStep.INITIAL
		) {
			setShownAmounts({
				totalAmount: requestShipmentData.totalAmount,
				bankFee: requestShipmentData.bankFee,
			});
			setShipmentDialogStep(EShipmentDialogStep.PREVIEW);
		}
	}, [requestShipmentData, open, requestEuDdpShipmentLoader, shipmentDialogStep]);

	useEffect(() => {
		if (
			prevRequestShipmentLoading.current &&
			!requestEuDdpShipmentLoader &&
			requestShipmentError &&
			open
		) {
			setShipmentDialogStep(EShipmentDialogStep.INITIAL);
			setClose();
		}
		prevRequestShipmentLoading.current = requestEuDdpShipmentLoader;
	}, [requestEuDdpShipmentLoader, requestShipmentError, open, setClose]);

	useEffect(() => {
		if (
			prevConfirmShipmentLoading.current &&
			!confirmEuDdpShipmentLoader &&
			!confirmShipmentError &&
			open
		) {
			setShipmentDialogStep(EShipmentDialogStep.INITIAL);
			dispatch(setEuDdpConfirmShipmentError(null));
			dispatch(clearEuDdpRequestShipmentData());
			setClose();
		}
		prevConfirmShipmentLoading.current = confirmEuDdpShipmentLoader;
	}, [confirmEuDdpShipmentLoader, confirmShipmentError, open, dispatch, setClose]);

	useEffect(() => {
		if (prevS10Loading.current && !s10Loading && !s10Error && open) {
			setShipmentDialogStep(EShipmentDialogStep.INITIAL);
			setS10Number('');
			dispatch(clearEuDdpRequestShipmentData());
			setClose();
		}
		prevS10Loading.current = s10Loading;
	}, [s10Loading, s10Error, open, dispatch, setClose]);

	const handleClose = () => {
		setShipmentDialogStep(EShipmentDialogStep.INITIAL);
		setS10Number('');
		dispatch(setEuDdpConfirmShipmentError(null));
		dispatch(setEuDdpS10Error(null));
		dispatch(clearEuDdpRequestShipmentData());
		dispatch(
			getEuDdpOrderRequest({
				orderId: Number(orderId),
			}),
		);
		setClose();
	};

	const handleConfirm = () => {
		if (shipmentDialogStep !== EShipmentDialogStep.PREVIEW || !order) return;
		dispatch(
			confirmEuDdpShipmentRequest({
				orderId: order.order.id,
				expectedTotalAmount: shownAmounts?.totalAmount ?? null,
				expectedBankFee: shownAmounts?.bankFee ?? null,
			}),
		);
	};

	const handleProvideS10Number = () => {
		dispatch(setEuDdpS10Error(null));
		setShipmentDialogStep(EShipmentDialogStep.S10_EXPECTED);
	};

	const handleSubmitS10 = () => {
		if (!order || !s10Number.trim()) return;
		dispatch(
			requestEuDdpS10Request({
				orderId: order.order.id,
				s10Number: s10Number.trim(),
				expectedTotalAmount: shownAmounts?.totalAmount ?? null,
				expectedBankFee: shownAmounts?.bankFee ?? null,
			}),
		);
	};

	const title =
		shipmentDialogStep === EShipmentDialogStep.INITIAL
			? 'Calculating delivery cost'
			: shipmentDialogStep === EShipmentDialogStep.S10_EXPECTED
				? 'Provide S10 Number'
				: 'Review and Confirm the delivery';

	const isPreviewStep = shipmentDialogStep === EShipmentDialogStep.PREVIEW;
	const isS10Step = shipmentDialogStep === EShipmentDialogStep.S10_EXPECTED;
	const showProvideS10Button = isPreviewStep && !!requestShipmentData?.s10Expected;
	const showConfirmButton = isPreviewStep && !requestShipmentData?.s10Expected;

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">{title}</DialogTitle>
			<DialogContent>
				{shipmentDialogStep === EShipmentDialogStep.INITIAL && <StepInitial />}
				{shipmentDialogStep === EShipmentDialogStep.PREVIEW && <StepPreview />}
				{shipmentDialogStep === EShipmentDialogStep.S10_EXPECTED && (
					<StepS10Expected s10Number={s10Number} setS10Number={setS10Number} />
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={actionLoading}>
					Cancel
				</Button>
				{showProvideS10Button && (
					<Button
						onClick={handleProvideS10Number}
						autoFocus
						disabled={actionLoading}
						variant="contained"
					>
						Provide S10 Number
					</Button>
				)}
				{showConfirmButton && (
					<Button
						onClick={handleConfirm}
						autoFocus
						disabled={!requestShipmentData || actionLoading}
						variant="contained"
					>
						{confirmEuDdpShipmentLoader ? (
							<CircularProgress size={20} />
						) : (
							'Confirm and create label'
						)}
					</Button>
				)}
				{isS10Step && (
					<Button
						onClick={handleSubmitS10}
						autoFocus
						disabled={!s10Number.trim() || actionLoading}
						variant="contained"
					>
						{s10Loading ? <CircularProgress size={20} /> : 'Submit S10 Number'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
